import { CaptionFeed, type FeedLine, type FeedState } from "../shared/feed";
import { LANG_NAMES, t } from "./i18n";
import { WakeLock, roomFromPath, store } from "../shared/backoff";

const MAX_LINES = 400;

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const statusEl = $("status");
const statusText = $("status-text");
const main = $("main");
const captions = $("captions");
const interimEl = $("interim");
const waiting = $("waiting");
const jump = $<HTMLButtonElement>("jump");
const settings = $<HTMLDialogElement>("settings");
const sizeInput = $<HTMLInputElement>("size");
const wake = new WakeLock();

// ---------- language: the reader's own choice, else ?lang=uk (QR codes), else the phone's own language

const offeredDefault = Object.keys(LANG_NAMES);
function initialLang(): string {
  // A choice made in the sheet wins; the QR code's ?lang= only sets the starting language.
  const saved = store.get("lc.lang");
  if (saved && offeredDefault.includes(saved)) return saved;
  const q = new URLSearchParams(location.search).get("lang");
  if (q && offeredDefault.includes(q)) {
    store.set("lc.lang", q);
    return q;
  }
  const first = (navigator.languages?.[0] ?? navigator.language ?? "en").toLowerCase().split("-")[0];
  return offeredDefault.includes(first) ? first : "en";
}
let lang = initialLang();
let offered: string[] = ["en"];
let feedState: FeedState = "connecting";

function applyStrings() {
  const s = t(lang);
  document.documentElement.lang = lang;
  captions.lang = lang;
  const set = (id: string, text: string) => {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  };
  $("settings-btn").setAttribute("aria-label", s.settingsAria);
  set("lang-code", s.code);
  set("mt-code", s.code);
  $("lang-strip").setAttribute("aria-label", s.otherLanguages);
  set("t-waiting-title", s.waitingTitle);
  set("t-waiting-body", s.waitingBody);
  set("feedback-q", s.feedbackQ);
  set("feedback-thanks", s.thanks);
  set("settings-title", s.display);
  set("t-language", s.language);
  set("t-text-size", s.textSize);
  set("t-theme", s.theme);
  set("t-dark", s.dark);
  set("t-light", s.light);
  set("t-contrast", s.contrast);
  jump.textContent = s.jump;
  document.querySelector("#settings .done")!.textContent = s.done;
  document.querySelector("#settings .grab")?.setAttribute("aria-label", s.closeSheet);
  for (const b of document.querySelectorAll<HTMLButtonElement>("button[data-rating]")) {
    b.textContent = s[b.dataset.rating as "good" | "ok" | "poor"];
  }
  set("mt-text", s.machineNote);
  $("mt-note").hidden = !s.machineNote;
  renderStatus();
  renderStrip();
}

/** Other-language buttons (each named in its own language). */
function langButtons(codes: string[]): HTMLButtonElement[] {
  return codes.map((code) => {
    const b = document.createElement("button");
    b.type = "button";
    b.lang = code;
    // Switches in place (the reader's stored choice beats ?lang= links), so a toggle, not a link.
    b.setAttribute("aria-pressed", "false");
    b.textContent = LANG_NAMES[code];
    b.addEventListener("click", () => chooseLang(code));
    return b;
  });
}

// English readers get the other languages at the top of the captions, so someone who
// arrives mid-sermon has a way in that doesn't depend on guessing what "Aa" means.
// They scroll away with the first lines, and don't come back after a Clear.
let stripGoneFor: string | undefined;
function renderStrip() {
  const strip = $("lang-strip");
  const show =
    lang === "en" && offered.length > 1 && captions.childElementCount > 0 && feed?.currentEpoch !== stripGoneFor;
  if (show && !strip.childElementCount) strip.replaceChildren(...langButtons(offered.filter((c) => c !== "en")));
  strip.hidden = !show;
}

/** The language choices in the display sheet (and the strip atop English captions). */
let renderedLangs = "";
function renderLangs(langs: string[]) {
  offered = langs.filter((l) => l in LANG_NAMES);
  if (!offered.includes(lang)) return chooseLang("en");
  // Called on every status poll: only rebuild when something changed (keeps focus in the open sheet).
  const key = `${lang}|${offered.join(",")}`;
  if (key === renderedLangs) return;
  renderedLangs = key;
  const many = offered.length > 1;
  $("lang-field").hidden = !many;
  // Fixed order (English first), each row in its own language.
  $("langs").replaceChildren(
    ...offered.map((code) => {
      const own = t(code);
      const label = document.createElement("label");
      label.lang = code;
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "lang";
      input.value = code;
      input.checked = code === lang;
      input.addEventListener("change", () => input.checked && chooseLang(code));
      const name = document.createElement("span");
      name.className = "name";
      const b = document.createElement("b");
      b.textContent = LANG_NAMES[code];
      name.append(b);
      if (own.autoTranslation) {
        const small = document.createElement("small");
        small.textContent = own.autoTranslation;
        name.append(small);
      }
      const codeEl = document.createElement("span");
      codeEl.className = "code";
      codeEl.setAttribute("aria-hidden", "true");
      codeEl.textContent = own.code;
      label.append(input, name, codeEl);
      return label;
    }),
  );
  $("lang-strip").replaceChildren();
  renderStrip();
}

function chooseLang(code: string) {
  if (code === lang) return;
  lang = code;
  store.set("lc.lang", code);
  applyStrings();
  renderLangs(offered);
  resetFloor();
  captions.replaceChildren();
  interimEl.textContent = "";
  showFeedback(false);
  feed.setLang(code);
}

// ---------- display settings

const root = document.documentElement;
function applyTheme(t: string) {
  root.dataset.theme = t;
  store.set("lc.theme", t);
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute("content", t === "light" ? "#f4f2ee" : t === "contrast" ? "#000000" : "#0e0f10");
}
function applyScale(s: string) {
  root.style.setProperty("--scale", s);
  // Large text: the line being spoken is capped to its last two lines (live.css).
  root.classList.toggle("big", Number(s) >= 2.5);
  store.set("lc.scale", s);
}
const initialTheme =
  store.get("lc.theme") ?? (window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark");
applyTheme(initialTheme);
sizeInput.value = store.get("lc.scale") ?? "1.5";
applyScale(sizeInput.value);
for (const r of document.querySelectorAll<HTMLInputElement>('input[name="theme"]')) {
  r.checked = r.value === initialTheme;
  r.addEventListener("change", () => r.checked && applyTheme(r.value));
}
sizeInput.addEventListener("input", () => {
  const stick = atBottom;
  applyScale(sizeInput.value);
  resetFloor();
  if (stick) scrollToEnd();
});
$("settings-btn").addEventListener("click", () => {
  if (typeof settings.showModal === "function") settings.showModal();
  else settings.setAttribute("open", "");
});
function closeSettings() {
  if (typeof settings.close === "function") settings.close();
  else settings.removeAttribute("open");
}
settings.addEventListener("close", () => (settings.style.transform = ""));
// Tapping the dimmed page above the sheet closes it.
settings.addEventListener("click", (e) => {
  const r = settings.getBoundingClientRect();
  if (e.clientY < r.top || e.clientX < r.left || e.clientX > r.right) closeSettings();
});
// The handle: drag the sheet down to close it, or tap it. The sheet follows the finger; no animation.
const grab = settings.querySelector<HTMLElement>(".grab");
if (grab) {
  let startY: number | null = null;
  let dy = 0;
  let dragged = false;
  grab.addEventListener("pointerdown", (e) => {
    startY = e.clientY;
    dy = 0;
    dragged = false;
    try {
      grab.setPointerCapture(e.pointerId);
    } catch {}
  });
  grab.addEventListener("pointermove", (e) => {
    if (startY === null) return;
    dy = Math.max(0, e.clientY - startY);
    if (dy > 4) dragged = true;
    settings.style.transform = `translateY(${dy}px)`;
  });
  const release = () => {
    if (startY === null) return;
    startY = null;
    if (dy > 60) closeSettings();
    else settings.style.transform = "";
  };
  grab.addEventListener("pointerup", release);
  grab.addEventListener("pointercancel", () => {
    startY = null;
    settings.style.transform = "";
  });
  // A tap (or Enter/Space on the keyboard) closes; the click that follows a short drag doesn't.
  grab.addEventListener("click", () => {
    if (!dragged) closeSettings();
    dragged = false;
  });
}

// ---------- auto-scroll with "jump to live"

let atBottom = true;
const nearBottom = () => window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 80;
function scrollToEnd() {
  window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "auto" });
}
let programmaticScroll = false;
window.addEventListener(
  "scroll",
  () => {
    if (programmaticScroll) return;
    atBottom = nearBottom();
    jump.hidden = atBottom;
  },
  { passive: true },
);
jump.addEventListener("click", () => {
  atBottom = true;
  jump.hidden = true;
  scrollToEnd();
});
// The caption area never gets shorter during a session. While a sentence is being spoken
// its words get revised, and a revision can make it a line shorter; if the page shrank,
// everything above would jump down. Instead the space stays as a blank line until new
// words fill it. Reset only when the layout really changes (new session, text size, rotation).
let floor = 0;
function holdHeight() {
  main.style.minHeight = "";
  const natural = main.offsetHeight;
  floor = Math.max(floor, natural);
  if (floor > natural) main.style.minHeight = `${floor}px`;
}
function resetFloor() {
  floor = 0;
  main.style.minHeight = "";
}
let lastWidth = window.innerWidth;
window.addEventListener("resize", () => {
  if (window.innerWidth === lastWidth) return; // mobile URL bar showing/hiding changes height only
  lastWidth = window.innerWidth;
  resetFloor();
  if (atBottom) scrollToEnd();
});

function afterContentChange() {
  holdHeight();
  if (atBottom) {
    programmaticScroll = true;
    scrollToEnd();
    requestAnimationFrame(() => (programmaticScroll = false));
  } else {
    jump.hidden = false;
  }
}

// ---------- rendering

// After the desk presses Clear, the next line can start mid-sentence ("he gave his one and only
// son."): a muted "…" shows it continues something that was cleared.
let afterClear = false;

function lineEl(l: FeedLine): HTMLParagraphElement {
  const p = document.createElement("p");
  p.dataset.id = String(l.id);
  if (l.fallback) {
    // Translation failed, so this is the English line: tag it, and let screen readers switch voice.
    p.lang = "en";
    const tag = document.createElement("span");
    tag.className = "src-tag";
    tag.setAttribute("aria-hidden", "true");
    tag.textContent = "EN";
    p.append(tag);
  }
  p.append(l.text);
  return p;
}

function markContinuation(p: HTMLParagraphElement, text: string) {
  const first = text.trimStart().charAt(0);
  if (first && first !== first.toUpperCase() && first === first.toLowerCase()) {
    const cont = document.createElement("span");
    cont.className = "cont";
    cont.setAttribute("aria-hidden", "true");
    cont.textContent = "…";
    p.prepend(cont);
  }
}

// ---------- feedback after a session ends (anonymous counts, one rating per phone per session)

// Optional: an old cached page without the section must still show captions.
const feedbackEl = document.getElementById("feedback");
const voteKey = (epoch: string) => `lc.voted.${epoch}`;
function showFeedback(show: boolean) {
  if (!feedbackEl) return;
  const epoch = feed.currentEpoch;
  const want = show && !!epoch && captions.childElementCount > 0 && !store.get(voteKey(epoch));
  if (feedbackEl.hidden === !want) return;
  feedbackEl.hidden = !want;
  feedbackEl.querySelector<HTMLElement>(".feedback-thanks")!.hidden = true;
  feedbackEl.querySelector<HTMLElement>(".feedback-buttons")!.hidden = false;
  afterContentChange();
}
for (const b of feedbackEl?.querySelectorAll<HTMLButtonElement>("button[data-rating]") ?? []) {
  b.addEventListener("click", () => {
    const epoch = feed.currentEpoch;
    if (!epoch) return;
    store.set(voteKey(epoch), b.dataset.rating!);
    feedbackEl!.querySelector<HTMLElement>(".feedback-buttons")!.hidden = true;
    feedbackEl!.querySelector<HTMLElement>(".feedback-thanks")!.hidden = false;
    fetch(`/api/${roomFromPath()}/feedback`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ epoch, rating: b.dataset.rating }),
    }).catch(() => {});
  });
}

function renderStatus() {
  const s = t(lang);
  const text: Record<FeedState, string> = {
    connecting: s.connecting,
    waiting: s.notStarted,
    idle: s.notStarted,
    live: s.live,
    paused: s.paused,
    reconnecting: s.reconnecting,
  };
  // After Stop the last service's captions stay readable; say it's over rather than "not started".
  statusText.textContent = feedState === "waiting" && captions.childElementCount > 0 ? s.ended : text[feedState];
}

const feed = new CaptionFeed(roomFromPath(), {
  onLangs: renderLangs,
  onState(s) {
    if (s === "waiting") resetFloor();
    feedState = s;
    statusEl.dataset.state = s;
    renderStatus();
    showFeedback(s === "waiting");
    waiting.hidden = s !== "waiting" || captions.childElementCount > 0;
    if (s === "live" || s === "paused" || s === "reconnecting") wake.enable();
    else wake.disable();
  },
  onReset(lines) {
    resetFloor();
    showFeedback(false);
    // An empty reset mid-session is the desk's Clear.
    const cleared = lines.length === 0 && captions.childElementCount > 0 && feedState !== "waiting";
    if (cleared) {
      afterClear = true;
      stripGoneFor = feed.currentEpoch;
    }
    captions.replaceChildren(...lines.map(lineEl));
    renderStrip();
    waiting.hidden = true;
    afterContentChange();
  },
  onFinal(l) {
    const p = lineEl(l);
    if (afterClear) markContinuation(p, l.text);
    afterClear = false;
    captions.appendChild(p);
    renderStrip();
    if (captions.childElementCount > MAX_LINES) {
      while (captions.childElementCount > MAX_LINES) captions.firstElementChild?.remove();
      resetFloor();
    }
    waiting.hidden = true;
    afterContentChange();
  },
  onInterim(text) {
    if (interimEl.textContent === text) return;
    interimEl.textContent = text;
    afterContentChange();
  },
}, lang);
applyStrings();
feed.start();
